import sadEmoji from '@/assets/svgIcons/negative.svg'
import neutralEmoji from '@/assets/svgIcons/neutral.svg'
import happyEmoji from '@/assets/svgIcons/positive.svg'
import {
    borderVariant38,
    borderVariant40,
    borderVariant41,
} from '@/chakraTheme'

const feedbackThresholdMinutes = 5

const feedbackTypes = {
    STUDENT: 'student',
    MENTOR: 'mentor',
}

const feedbackStates = {
    BAD: 'bad',
    NEUTRAL: 'neutral',
    GOOD: 'good',
}

const questionSubtext = 'Please select all the applicable reasons'

const studentEmojiData = [
    {
        name: feedbackStates.BAD,
        border: borderVariant38,
        bgColor: 'wepeep2',
        emojiSrc: sadEmoji,
        animationSrc: '/animations/negative.lottie',
        question: 'What went wrong?',
        questionSubtext: questionSubtext,
        hasCategories: true,
        categories: [
            {
                mentor: {
                    title: 'Mentor',
                    options: [
                        {
                            mentor_did_not_resolve_my_doubts:
                                'Mentor didn’t resolve my doubts',
                        },
                        {
                            mentor_seemed_unprepared:
                                'Mentor seemed unprepared',
                        },
                        {
                            session_was_rushed: 'The session was rushed',
                        },
                        {
                            session_was_not_engaging:
                                'The session was not engaging',
                        },
                        {
                            mentor_did_not_set_context_for_the_topic:
                                'Mentor didn’t set context for the topic',
                        },
                    ],
                },
            },
            {
                content: {
                    title: 'Content',
                    options: [
                        {
                            content_was_not_well_structured:
                                'Content was not well-structured',
                        },
                        {
                            content_did_not_help_my_understanding:
                                'Content didn’t help my understanding',
                        },
                    ],
                },
            },
            {
                others: {
                    title: 'Others',
                    options: [
                        {
                            faced_platform_issues: 'Faced platform issues',
                        },
                        {
                            internet_was_bad: 'Internet was bad',
                        },
                        {
                            other_classroom_issues: 'Other classroom issues',
                        },
                    ],
                },
            },
        ],
    },
    {
        name: feedbackStates.NEUTRAL,
        border: borderVariant40,
        bgColor: 'pearllusta',
        emojiSrc: neutralEmoji,
        animationSrc: '/animations/neutral.lottie',
        question: 'What could be improved?',
        questionSubtext: questionSubtext,
        hasCategories: true,
        categories: [
            {
                mentor: {
                    title: 'Mentor',
                    options: [
                        {
                            mentors_preparation_could_have_been_better:
                                'Mentor’s preparation could’ve been better',
                        },
                        {
                            mentor_seemed_unprepared:
                                'Mentor seemed unprepared',
                        },
                        {
                            session_felt_rushed: 'Session felt rushed',
                        },
                        {
                            mentor_did_not_set_context_for_the_topic:
                                'Mentor didn’t set context for the topic',
                        },
                        {
                            some_of_my_doubts_were_unresolved:
                                'Some of my doubts were unresolved',
                        },
                    ],
                },
            },
            {
                content: {
                    title: 'Content',
                    options: [
                        {
                            content_could_be_better_structured:
                                'Content could be better structured',
                        },
                        {
                            content_did_not_help_my_understanding:
                                'Content didn’t help my understanding',
                        },
                    ],
                },
            },
        ],
    },
    {
        name: feedbackStates.GOOD,
        border: borderVariant41,
        bgColor: 'panache',
        emojiSrc: happyEmoji,
        animationSrc: '/animations/positive.lottie',
        question: 'What went well?',
        questionSubtext: questionSubtext,
        hasCategories: true,
        categories: [
            {
                mentor: {
                    title: 'Mentor',
                    options: [
                        {
                            mentor_was_well_prepared:
                                'Mentor was well prepared',
                        },
                        {
                            mentor_resolved_my_doubts:
                                'Mentor resolved my doubts',
                        },

                        {
                            session_was_engaging: 'Session was engaging',
                        },
                        {
                            mentor_set_context_for_the_topic:
                                'Mentor set the context for the topic',
                        },
                    ],
                },
            },
            {
                content: {
                    title: 'Content',
                    options: [
                        {
                            content_was_well_structured:
                                'Content was well-structured',
                        },
                        {
                            content_helped_my_understanding:
                                'Content helped my understanding',
                        },
                    ],
                },
            },
        ],
    },
]

const mentorEmojiData = [
    {
        name: feedbackStates.BAD,
        border: borderVariant38,
        bgColor: 'wepeep2',
        emojiSrc: sadEmoji,
        animationSrc: '/animations/negative.lottie',
        question: 'What went wrong?',
        questionSubtext: questionSubtext,
        hasCategories: false,
        options: [
            {
                tutor_notes_were_subpar: 'Tutor notes were sub-par',
            },
            {
                limited_or_no_preparation_time: 'Limited/no preparation time',
            },
            {
                no_training_was_provided: 'No training was provided',
            },
            {
                the_training_provided_was_insufficient:
                    'The training provided was insufficient',
            },
            {
                i_was_not_able_to_resolve_doubts:
                    'I wasn’t able to resolve doubts',
            },
            {
                session_duration_was_inadequate:
                    'Session duration was inadequate',
            },
        ],
    },
    {
        name: feedbackStates.NEUTRAL,
        border: borderVariant40,
        bgColor: 'pearllusta',
        emojiSrc: neutralEmoji,
        animationSrc: '/animations/neutral.lottie',
        question: 'What could be improved?',
        questionSubtext: questionSubtext,
        hasCategories: false,
        options: [
            {
                tutor_notes_need_more_clarity: 'Tutor notes need more clarity',
            },
            {
                preparation_time_could_be_more:
                    'Preparation time could be more',
            },
            {
                the_training_provided_could_have_been_better:
                    'The training provided could have been better',
            },
            {
                need_more_resources_for_doubt_resolution:
                    'Need more resources for doubt-resolution',
            },
        ],
    },
    {
        name: feedbackStates.GOOD,
        border: borderVariant41,
        bgColor: 'panache',
        emojiSrc: happyEmoji,
        animationSrc: '/animations/positive.lottie',
        question: 'What went well?',
        questionSubtext: questionSubtext,
        hasCategories: false,
        options: [
            {
                tutor_notes_were_helpful: 'Tutor notes were helpful',
            },
            {
                preparation_time_was_adequate: 'Preparation time was adequate',
            },
            {
                the_training_was_adequate: 'The training was adequate',
            },
            {
                i_resolved_all_students_doubts:
                    'I resolved all students’ doubts',
            },
            {
                session_duration_was_just_right:
                    'Session duration was just right',
            },
        ],
    },
]

export {
    feedbackStates,
    feedbackThresholdMinutes,
    feedbackTypes,
    mentorEmojiData,
    studentEmojiData,
}
